import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { CopyPasteConfig } from "./types";
import { useForm } from "react-final-form";
import copy from "copy-to-clipboard";
// buildReplacement больше не нужен - ID очищаются при копировании

/**
 * Рекурсивно удаляет ID поля (id, _id) и исключенные поля из объекта перед копированием
 *
 * @param obj - объект для очистки
 * @param excludeFields - поля для исключения при копировании
 * @returns очищенный объект без ID полей и исключенных полей
 */
function cleanupIdsForCopy<T>(obj: T, excludeFields: string[] = []): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => cleanupIdsForCopy(item, excludeFields)) as T;
  }

  if (typeof obj === "object") {
    // Конвертируем moment объекты в ISO строки для буфера обмена
    if ((obj as any)._isAMomentObject) {
      return (obj as any).toISOString() as T;
    }

    // Создаем новый объект, исключая ID поля и excludeFields
    const fieldsToExclude = new Set(["id", "_id", ...excludeFields]);
    const cleaned = {} as any;

    Object.keys(obj).forEach((key) => {
      if (!fieldsToExclude.has(key)) {
        cleaned[key] = (obj as any)[key];
      }
    });

    // Рекурсивно очищаем все свойства объекта
    for (const key in cleaned) {
      if (Object.prototype.hasOwnProperty.call(cleaned, key)) {
        cleaned[key] = cleanupIdsForCopy(cleaned[key], excludeFields);
      }
    }

    return cleaned;
  }

  return obj;
}

export function useCopyPaste(
  parentArrayField: string,
  config: CopyPasteConfig = {}
) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const form = useForm();

  const handleCopy = (item: any) => {
    // Очищаем ID и исключенные поля перед копированием, сохраняя moment объекты
    const cleanedItem = cleanupIdsForCopy(item, config.excludeFields || []);

    // Добавляем мета-информацию для защиты от вставки в неправильный массив
    const itemWithMeta = {
      __copyMeta: {
        sourceArrayField: parentArrayField,
        copiedAt: new Date().toISOString(),
      },
      ...cleanedItem,
    };

    const stringItem = JSON.stringify(itemWithMeta, null, 2);
    const didCopy = copy(stringItem);

    if (didCopy) {
      enqueueSnackbar(
        t("shared.form.copyPaste.copied", {
          defaultValue: "Добавлено в буфер обмена",
        }),
        { variant: "success" }
      );
    }
  };

  const handlePaste = async (index: number) => {
    try {
      if (!navigator.clipboard?.readText) {
        throw new Error(
          t("shared.form.copyPaste.clipboardUnavailable", {
            defaultValue: "Clipboard API недоступен",
          })
        );
      }

      const copiedObj = await navigator.clipboard.readText();
      const parsedRaw = JSON.parse(copiedObj);

      if (
        !parsedRaw ||
        typeof parsedRaw !== "object" ||
        Array.isArray(parsedRaw)
      ) {
        throw new SyntaxError(
          t("shared.form.copyPaste.notAnObject", {
            defaultValue: "В буфере не объект JSON",
          })
        );
      }

      // Проверяем мета-информацию для защиты от вставки в неправильный массив
      if (parsedRaw.__copyMeta) {
        const { sourceArrayField } = parsedRaw.__copyMeta;
        if (sourceArrayField !== parentArrayField) {
          throw new Error(
            t("shared.form.copyPaste.wrongSource", {
              defaultValue:
                'Нельзя вставить элемент из "{{source}}" в "{{target}}"',
              source: sourceArrayField,
              target: parentArrayField,
            })
          );
        }
        // Удаляем мета-данные перед вставкой
        delete parsedRaw.__copyMeta;
      } else {
        // Если нет мета-данных, предупреждаем пользователя
        console.warn(
          "Вставляется объект без мета-данных, возможно из старой копии"
        );
      }

      const state = form.getState();
      const list: any[] | undefined = state.values?.[parentArrayField];

      if (!Array.isArray(list)) {
        throw new Error(
          t("shared.form.copyPaste.arrayMissing", {
            defaultValue: 'В форме нет массива по пути "{{path}}"',
            path: parentArrayField,
          })
        );
      }

      if (index < 0 || index >= list.length) {
        throw new Error(
          t("shared.form.copyPaste.indexOutOfRange", {
            defaultValue:
              "Индекс {{index}} выходит за границы массива (длина: {{length}})",
            index,
            length: list.length,
          })
        );
      }

      const originalItem = list[index];

      // Заменяем данные, сохраняя ID и excludeFields из оригинального элемента
      const updatedItem = {
        ...parsedRaw,
        // Сохраняем ID из оригинального элемента для связи с бэкендом
        ...(originalItem.id && { id: originalItem.id }),
        ...(originalItem._id && { _id: originalItem._id }),
      };

      // Сохраняем excludeFields из оригинального элемента куда вставляем
      (config.excludeFields || []).forEach((field) => {
        if (field in originalItem) {
          updatedItem[field] = originalItem[field];
        }
      });

      const next = [...list];
      next[index] = updatedItem;

      form.batch(() => {
        form.change(parentArrayField, next);
      });

      enqueueSnackbar(
        t("shared.form.copyPaste.pasted", {
          defaultValue: "Объект вставлен из буфера",
        }),
        { variant: "success" }
      );
    } catch (e) {
      enqueueSnackbar(
        e instanceof SyntaxError
          ? t("shared.form.copyPaste.invalidJson", {
              defaultValue: "В буфере невалидный JSON",
            })
          : (e as Error)?.message ||
              t("shared.form.copyPaste.readFailed", {
                defaultValue: "Не удалось прочитать буфер обмена",
              }),
        { variant: "error" }
      );
      console.error(e);
    }
  };

  return { handleCopy, handlePaste };
}
